import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  Divider,
  Grid,
  Alert,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress
} from '@mui/material';
import {
  Close as CloseIcon,
  Palette as PaletteIcon,
  Folder as FolderIcon,
  Add as AddIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import { Group, GroupFormData, Team } from '../../types';
import { useDashboard } from '../../hooks/useDashboard';
import { useAuth } from '../../contexts/AuthContext';
import ColorPicker from '../Theme/ColorPicker';
import IconPicker from '../IconPicker/IconPicker';
import { IconRenderer } from '../IconRenderer';
import teamsApi from '../../services/teamsApi';

interface GroupEditDialogProps {
  open: boolean;
  onClose: () => void;
  group?: Group | null;
  mode: 'create' | 'edit';
  customOnSubmit?: (data: GroupFormData) => Promise<void>;
  previewMode?: boolean;
}

// Simplified schema without sharing fields
const schema: yup.ObjectSchema<Omit<GroupFormData, 'shareWithOthers' | 'sharedEmails' | 'selectedTeam'>> = yup.object({
  name: yup.string().required('Name is required').min(1, 'Name must be at least 1 character'),
  icon: yup.string().required('Icon is required'),
  color: yup.string().required('Color is required').matches(/^#[0-9A-F]{6}$/i, 'Please enter a valid hex color'),
  isCollapsible: yup.boolean().optional(),
  isCollapsed: yup.boolean().optional(),
  tabId: yup.string().optional(),
});

// Safe hook wrappers that return null if context is unavailable
const useSafeDashboard = () => {
  try {
    return useDashboard();
  } catch (e) {
    return { createGroup: null, updateGroup: null };
  }
};

const useSafeAuth = () => {
  try {
    return useAuth();
  } catch (e) {
    return { user: null };
  }
};

const GroupEditDialog: React.FC<GroupEditDialogProps> = ({
  open,
  onClose,
  group = null,
  mode,
  customOnSubmit,
  previewMode = false
}) => {
  const { createGroup, updateGroup } = useSafeDashboard();
  const { user } = useSafeAuth();

  const [showIconPicker, setShowIconPicker] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isValid }
  } = useForm<Omit<GroupFormData, 'shareWithOthers' | 'sharedEmails' | 'selectedTeam'>>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      name: group?.name || '',
      icon: group?.icon || 'folder',
      color: group?.color || '#1976d2',
      isCollapsible: group?.isCollapsible ?? true,
      isCollapsed: group?.isCollapsed ?? false,
    },
    mode: 'onChange'
  });

  const watchedColor = watch('color');
  const watchedIcon = watch('icon');
  const watchedName = watch('name');

  const handleColorChange = (color: string) => {
    setValue('color', color, { shouldValidate: true });
  };

  const handleIconSelect = (icon: string) => {
    setValue('icon', icon, { shouldValidate: true });
    setShowIconPicker(false);
  };

  const handleClose = () => {
    reset();
    setShowIconPicker(false);
    onClose();
  };

  const onSubmit: SubmitHandler<Omit<GroupFormData, 'shareWithOthers' | 'sharedEmails' | 'selectedTeam'>> = async (data) => {
    setIsSubmitting(true);
    try {
      if (customOnSubmit) {
        // Use custom handler for preview mode
        await customOnSubmit({
          ...data,
          shareWithOthers: false,
          sharedEmails: [],
        });
      } else {
        // Use dashboard methods for normal mode
        if (mode === 'create') {
          if (!createGroup) {
            throw new Error('Dashboard context not available');
          }
          await createGroup({
            name: data.name,
            icon: data.icon,
            color: data.color,
            isCollapsible: data.isCollapsible,
            isCollapsed: data.isCollapsed,
            shareWithOthers: false,
            sharedEmails: [],
          });
        } else if (group) {
          if (!updateGroup) {
            throw new Error('Dashboard context not available');
          }
          await updateGroup(group.id, {
            name: data.name,
            icon: data.icon,
            color: data.color,
            isCollapsible: data.isCollapsible,
            isCollapsed: data.isCollapsed,
            shareWithOthers: false,
            sharedEmails: [],
          });
        }
      }
      
      // Only close dialog after successful save
      handleClose();
    } catch (error) {
      console.error('Error saving group:', error);
      // Don't close dialog on error, let user try again
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Typography variant="h6" component="h2">
            {mode === 'create' ? 'Create New Group' : 'Edit Group'}
          </Typography>
          <Button
            onClick={handleClose}
            sx={{ minWidth: 'auto', p: 1, color: 'text.secondary' }}
          >
            <CloseIcon />
          </Button>
        </DialogTitle>

        <DialogContent sx={{ px: 3 }}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
            <Grid container spacing={3}>
              {/* Preview */}
              {previewMode && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Preview
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      backgroundColor: 'background.default'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 1,
                          backgroundColor: `${watchedColor}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: watchedColor
                        }}
                      >
                        <IconRenderer icon={watchedIcon} size="small" />
                      </Box>
                      <Typography variant="h6" sx={{ color: watchedColor, fontWeight: 600 }}>
                        {watchedName || 'Group Name'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      0 tiles in this group
                    </Typography>
                  </Box>
                </Grid>
              )}

              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Group Name"
                      fullWidth
                      error={!!errors.name}
                      helperText={errors.name?.message}
                      sx={{ mb: 3 }}
                    />
                  )}
                />

                {/* Group Settings */}
                <Typography variant="subtitle2" gutterBottom>
                  Group Settings
                </Typography>
                
                <Box sx={{ mb: 3 }}>
                  <Controller
                    name="isCollapsible"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} />}
                        label="Allow collapsing this group"
                        sx={{ display: 'block', mb: 1 }}
                      />
                    )}
                  />

                  <Controller
                    name="isCollapsed"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        control={<Switch {...field} checked={field.value} disabled={!watch('isCollapsible')} />}
                        label="Start collapsed by default"
                        sx={{ display: 'block' }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Appearance */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Appearance
                </Typography>
                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={2}>
                  {/* Color Picker */}
                  <Grid item xs={12} sm={6}>
                    <ColorPicker
                      label="Group Color"
                      value={watchedColor}
                      onChange={handleColorChange}
                    />
                  </Grid>

                  {/* Icon Picker */}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" gutterBottom>
                      Group Icon
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setShowIconPicker(true)}
                      fullWidth
                      startIcon={<IconRenderer icon={watchedIcon} size="small" />}
                      sx={{
                        justifyContent: 'flex-start',
                        height: 48,
                        textTransform: 'none'
                      }}
                    >
                      {watchedIcon || 'Select Icon'}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : (mode === 'create' ? 'Create Group' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Icon Picker Dialog */}
      <IconPicker
        open={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onIconSelect={handleIconSelect}
      />
    </>
  );
};

export default GroupEditDialog;